import React, { Component, Fragment } from 'react';
import { inject, observer } from 'mobx-react';
import b from 'b_';
import UDPoliciesStore from '../../stores/UDPolicies/UDPoliciesStore';
import SnackStore from '../../stores/SnackStore';
import Button from '../Button';
import { ConsoleCard, ConsoleCardHeader, ConsolePage, ConsoleTable, TextWithTitle } from '../ConsoleFragments';
import FuseInfoTooltip from '../FuseControls/FuseTooltip/FuseInfoTooltip';
import MoreMenu from '../Menu/MoreMenu';
import UDPolicyModal from './UDPolicyModal';
import DeleteUDPolicyModal from './DeleteUDPolicyModal';
import { ROLE_READ_ONLY } from '../../stores/Administrators/AdministratorModalStore';
import ConsoleStore from '../../stores/ConsoleStore';
import './ConsoleUDPolicies.css';

const block = b.lock('ConsoleUDPolicies');

class ConsoleUDPolicies extends Component {
    static propTypes = {
        console: ConsoleStore.PropType.isRequired,
        udPolicies: UDPoliciesStore.PropType.isRequired,
        snack: SnackStore.PropType.isRequired,
    };

    componentWillMount() {
        this.props.udPolicies.load()
            .catch((error) => this.props.snack.showError(`Failed to load policy list. ${error.message}`));
        this.props.udPolicies.loadLastSyncGroup();
    }

    saveOrder = () => {
        this.props.udPolicies.saveOrder()
            .catch((error) => this.props.snack.showError(`Failed to update priorities. ${error.message}`));
    };

    priorityColumn = {
        name: 'priority',
        title: 'Priority',
        render: (p, itemIndex) => itemIndex + 1,
    };

    nameColumn = {
        name: 'name',
        title: 'Policy Name',
        render: (p) => (p.isDefault ? <Fragment>
            <strong>Default Policy</strong>
            <FuseInfoTooltip inline>
                The default policy is a system generated policy.
                This policy will always be the lowest priority and cannot be deleted.
            </FuseInfoTooltip>
        </Fragment> : <TextWithTitle text={p.name} className={block('NameWrapper')} />),
    };

    defaultColumns = [this.priorityColumn, this.nameColumn, {
        name: 'enabled',
        title: 'Redirect Status',
        render: (p) => (p.enabled ? 'Enabled' : <span style={{ color: '#F44336' }}>Disabled</span>),
    }, {
        name: 'menu',
        title: '',
        render: (p) => {
            const readOnly = this.isReadOnly();

            return <MoreMenu
                className={block('MoreMenu')}
                menuStore={this.props.udPolicies.menu}
                menuId={p.id}
            >
                {!readOnly ? (
                    <MoreMenu.Item
                        icon={'edit'}
                        onClick={() => this.props.udPolicies.showUpdate(p)}
                    >Edit Policy…</MoreMenu.Item>
                ) : (
                    <MoreMenu.Item
                        icon={'view'}
                        onClick={() => this.props.udPolicies.showView(p)}
                    >View Policy…</MoreMenu.Item>
                )}
                <MoreMenu.Item
                    icon="delete"
                    onClick={() => this.props.udPolicies.showDeleteModal(p)}
                    disabled={p.isDefault || readOnly}
                >Delete Policy…</MoreMenu.Item>
            </MoreMenu>;
        },
    }];

    reorderingColumns = [this.priorityColumn, this.nameColumn, {
        name: 'reorder',
        title: 'Change Order',
        render: (p) => <Fragment>
            {this.props.udPolicies.canIncreasePriority(p) ? <Button
                className={block('Reorder', { up: true })}
                icon="up"
                onClick={() => this.props.udPolicies.orderUp(p)}
            /> : null}
            {this.props.udPolicies.canLowerPriority(p) ? <Button
                className={block('Reorder', { down: true })}
                icon="down"
                onClick={() => this.props.udPolicies.orderDown(p)}
            /> : null}
        </Fragment>,
    }, {
        name: 'orderTop',
        title: 'Top',
        render: (p) => (this.props.udPolicies.canIncreasePriority(p) ? <Button
            className={block('Reorder', { top: true })}
            icon="top"
            onClick={() => this.props.udPolicies.orderTop(p)}
        /> : null),
    }, {
        name: 'orderBottom',
        title: 'Bottom',
        render: (p) => (this.props.udPolicies.canLowerPriority(p) ? <Button
            className={block('Reorder', { bottom: true })}
            icon="bottom"
            onClick={() => this.props.udPolicies.orderBottom(p)}
        /> : null),
    }];

    renderDefaultView() {
        const { udPolicies } = this.props;

        return <Fragment>
            <ConsoleCardHeader title="Manage Policies">
                {!this.isReadOnly() ? <Fragment>
                    <Button
                        className={block('NewPolicy')}
                        raised
                        primary
                        icon="add"
                        style={{ marginLeft: 12 }}
                        onClick={udPolicies.showCreate}
                        disabled={udPolicies.listStatus.isLoading}
                    >NEW POLICY</Button>
                    <Button
                        className={block('ChangePriority')}
                        raised
                        primary
                        style={{ marginLeft: 12 }}
                        onClick={udPolicies.startReordering}
                        disabled={!udPolicies.canChangeOrder || udPolicies.listStatus.isLoading}
                    >CHANGE PRIORITY</Button>
                </Fragment> : null}
            </ConsoleCardHeader>
            <ConsoleTable
                columns={this.defaultColumns}
                loading={udPolicies.listStatus.isLoading}
                items={udPolicies.originalOrder}
                cellClassName={block('Cell')}
                focusedItemId={udPolicies.menu.id}
            />
        </Fragment>;
    }


    isReadOnly() {
        return this.props.console.authority === ROLE_READ_ONLY.value;
    }

    renderReorderingView() {
        const { udPolicies } = this.props;

        return <Fragment>
            <ConsoleCardHeader title="Change Policy Priorities">
                <Button
                    className={block('CancelPriorityChange')}
                    raised
                    primary
                    onClick={udPolicies.cancelReordering}
                    style={{ marginRight: 10 }}
                >CANCEL</Button>
                <Button
                    className={block('SavePriorities')}
                    raised
                    primaryGreen
                    loading={udPolicies.setOrderStatus.isLoading}
                    onClick={this.saveOrder}
                >DONE PRIORITIZING</Button>
            </ConsoleCardHeader>
            <ConsoleTable
                columns={this.reorderingColumns}
                items={udPolicies.newOrder}
                cellClassName={block('Cell')}
                focusedItemId={udPolicies.menu.id}
            />
        </Fragment>;
    }

    render() {
        return <ConsolePage title="URL Isolation — Policies">
            <ConsoleCard>
                {this.props.udPolicies.reordering
                    ? this.renderReorderingView()
                    : this.renderDefaultView()}
            </ConsoleCard>
            <UDPolicyModal />
            <DeleteUDPolicyModal />
        </ConsolePage>;
    }
}

export default inject('console', 'udPolicies', 'snack')(observer(ConsoleUDPolicies));

